// WHAT: Kanban board component for task management with drag-and-drop
// WHY:  Provides visual task management interface with status columns and drag-and-drop reordering
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: @dnd-kit for drag-and-drop, KanbanColumn, KanbanCard components
// RISKS:
//   - NOTE: Drag-and-drop may have performance issues with many tasks
//   - NOTE: Task status update on drag may fail silently
//   - NOTE: Filter search may not work correctly with special characters
//   - NOTE: Composite ID format may cause issues if task IDs contain pipe character

import type { SupabaseTask } from "../../hooks/data/useSupabasetasks";

import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
  LucideColumns,
  LucidePlus,
  LucideCheckCircle,
  LucideClock,
  LucideFolderOpen,
  LucideTarget,
  LucideBarChart3,
  LucideSearch,
} from "lucide-react";
import React, { type FC, useState, useMemo } from "react";

import { Card, Button } from "@/components/ui";
import { KanbanCard } from "@/domains/productivity/timetracq/components/molecules/KanbanCard";

/**
 * Props for TasksKanbanBoard component
 */
interface Properties {
  /** Delete task handler */
  deleteTask: (id: string) => Promise<void>;
  /** Set editing task handler */
  setEditingTask: (task: SupabaseTask | undefined) => void;
  /** Set preselected workspace handler (optional) */
  setPreselectedWorkspace?: (workspace: string | undefined) => void;
  /** Set show task modal handler */
  setShowTaskModal: (show: boolean) => void;
  /** Set task filter handler */
  setTaskFilter: (value: string) => void;
  /** Task filter string */
  taskFilter: string;
  /** List of tasks */
  tasks: SupabaseTask[];
  /** Update task handler */
  updateTask: (id: string, changes: Partial<SupabaseTask>) => Promise<void>;
  /** Workspace list (optional) */
  workspaceList?: string[];
}

/**
 * Default workspace list
 */
const defaultWorkspaces = [
  "Nexus",
  "Investments",
  "Trading",
  "ResponsumOS",
  "Real Estate",
  "TracQ",
];

/**
 * Status configuration interface
 */
interface StatusConfigType {
  bgColor: string;
  borderColor: string;
  color: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  textColor: string;
  title: string;
}

/**
 * Sorts tasks by score in descending order.
 *
 * @param tasks - Tasks to sort
 *
 * @returns Sorted tasks array
 *
 */
const sortTasksByScore = (tasks: SupabaseTask[]): SupabaseTask[] => {
  return tasks.toSorted((a: SupabaseTask, b: SupabaseTask): number => {
    const scoreA = a.score ?? 0;
    const scoreB = b.score ?? 0;
    return scoreB - scoreA;
  });
};

/**
 * Creates delete handler for a task.
 *
 * @param taskId - Task ID
 *
 * @param deleteTask - Delete task function
 *
 * @returns Delete handler function
 *
 */
const createDeleteHandler = (taskId: string, deleteTask: (id: string) => Promise<void>) => {
  return (): void => {
    if (confirm("Delete task?")) {
      void deleteTask(taskId);
    }
  };
};

/**
 * Creates edit handler for a task.
 *
 * @param task - Task to edit
 *
 * @param setEditingTask - Set editing task function
 *
 * @param setShowTaskModal - Set show task modal function
 *
 * @returns Edit handler function
 *
 */
const createEditHandler = (
  task: SupabaseTask,
  setEditingTask: (task: SupabaseTask | undefined) => void,
  setShowTaskModal: (show: boolean) => void,
) => {
  return (): void => {
    setEditingTask(task);
    setShowTaskModal(true);
  };
};

/**
 * Renders status columns for a workspace.
 *
 * @param filteredTasks - Filtered tasks
 *
 * @param statusConfig - Status configuration
 *
 * @param deleteTask - Delete task handler
 *
 * @param setEditingTask - Set editing task handler
 *
 * @param setShowTaskModal - Set show task modal handler
 *
 * @param handleDragStart - Handle drag start handler
 *
 * @param handleDragEnd - Handle drag end handler
 *
 * @param sensors - DnD sensors
 *
 * @param overlayTask - Overlay task
 *
 * @returns React.ReactElement
 *
 */
const renderStatusColumns = (
  filteredTasks: SupabaseTask[],
  statusConfig: Record<string, StatusConfigType>,
  deleteTask: (id: string) => Promise<void>,
  setEditingTask: (task: SupabaseTask | undefined) => void,
  setShowTaskModal: (show: boolean) => void,
  handleDragStart: (event: DragStartEvent) => void,
  handleDragEnd: (event: DragEndEvent) => Promise<void>,
  sensors: ReturnType<typeof useSensors>,
  overlayTask: SupabaseTask | undefined,
): React.ReactElement => {
  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement]}
      onDragEnd={(event) => {
        void handleDragEnd(event);
      }}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {Object.entries(statusConfig).map(([statusKey, config]) => {
          const filteredStatusItems: SupabaseTask[] = filteredTasks.filter(
            (t: SupabaseTask) => (t.status ?? "No Status") === statusKey,
          );
          const statusItems: SupabaseTask[] = sortTasksByScore(filteredStatusItems);
          const Icon = config.icon;

          return (
            <SortableContext
              items={statusItems.map((it: SupabaseTask): string => {
                return `${it.id}|${statusKey}`;
              })}
              key={statusKey}
              strategy={verticalListSortingStrategy}
            >
              <Card
                className={`bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-200 hover:scale-[1.02] group ${config.borderColor}/30`}
                variant="default"
              >
                {/* Column Header */}
                <div
                  className={`p-4 border-b border-slate-600/50 ${config.borderColor} border-l-4`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg transition-all duration-200 group-hover:scale-110 ${config.bgColor}/20`}
                    >
                      <Icon className={`w-4 h-4 ${config.textColor}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className={`text-sm font-semibold ${config.textColor}`}>
                        {config.title}
                      </h4>
                      <p className="text-slate-400 text-xs">{config.description}</p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded-full ${config.bgColor}/20 ${config.textColor}`}
                    >
                      {statusItems.length}
                    </span>
                  </div>
                </div>

                {/* Column Content */}
                <div className="p-3 space-y-3 max-h-80 overflow-y-auto">
                  {statusItems.length === 0 ? (
                    <div className="text-center py-6">
                      <div
                        className={`w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center ${config.bgColor}/20`}
                      >
                        <Icon className={`w-4 h-4 ${config.textColor}`} />
                      </div>
                      <p className="text-slate-500 text-xs italic">No tasks</p>
                    </div>
                  ) : (
                    statusItems.map((task: SupabaseTask) => {
                      return (
                        <div className="group/item" key={task.id}>
                          <KanbanCard
                            id={`${task.id}|${statusKey}`}
                            onDelete={createDeleteHandler(task.id, deleteTask)}
                            onEdit={createEditHandler(task, setEditingTask, setShowTaskModal)}
                            task={task}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </Card>
            </SortableContext>
          );
        })}
      </div>

      {/* Drag Overlay */}
      <DragOverlay dropAnimation={{ duration: 150, easing: "ease-in-out" }}>
        {overlayTask ? (
          <div className="transform rotate-3 scale-105">
            <KanbanCard isOverlay task={overlayTask} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

/**
 * Renders workspace kanban boards.
 *
 * @param workspaces - List of workspace names
 *
 * @param workspaceStats - Workspace statistics
 *
 * @param tasks - List of tasks
 *
 * @param taskFilter - Task filter string
 *
 * @param statusConfig - Status configuration
 *
 * @param deleteTask - Delete task handler
 *
 * @param setEditingTask - Set editing task handler
 *
 * @param setShowTaskModal - Set show task modal handler
 *
 * @param handleNewTask - Handle new task handler
 *
 * @param handleDragStart - Handle drag start handler
 *
 * @param handleDragEnd - Handle drag end handler
 *
 * @param sensors - DnD sensors
 *
 * @param overlayTask - Overlay task
 *
 * @returns React.ReactElement
 *
 */
const renderWorkspaceBoards = (
  workspaces: string[],
  workspaceStats: Record<string, { total: number; byStatus: Record<string, number> }>,
  tasks: SupabaseTask[],
  taskFilter: string,
  statusConfig: Record<string, StatusConfigType>,
  deleteTask: (id: string) => Promise<void>,
  setEditingTask: (task: SupabaseTask | undefined) => void,
  setShowTaskModal: (show: boolean) => void,
  handleNewTask: (workspace: string) => void,
  handleDragStart: (event: DragStartEvent) => void,
  handleDragEnd: (event: DragEndEvent) => Promise<void>,
  sensors: ReturnType<typeof useSensors>,
  overlayTask: SupabaseTask | undefined,
): React.ReactElement => {
  return (
    <div className="space-y-8">
      {workspaces.map((workspace: string) => {
        const stats = workspaceStats[workspace];
        if (!stats) {
          return null;
        }
        const totalItems = stats.total;
        const filteredTasks: SupabaseTask[] = tasks.filter(
          (t: SupabaseTask) =>
            t.workspace === workspace && t.task.toLowerCase().includes(taskFilter.toLowerCase()),
        );

        return (
          <div className="space-y-4" key={workspace}>
            {/* Workspace Header */}
            <Card
              className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-200 hover:scale-[1.01]"
              padding="lg"
              variant="elevated"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-tracq-bright-blue/20 rounded-lg">
                    <LucideTarget className="w-5 h-5 text-tracq-bright-blue" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{workspace}</h3>
                    <p className="text-slate-400 text-sm">
                      {totalItems} {totalItems === 1 ? "Task" : "Tasks"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {Object.entries(statusConfig).map(
                    ([statusKey, config]: [string, StatusConfigType]) => (
                      <span
                        className={`text-xs px-2 py-1 rounded-lg font-medium ${config.bgColor}/20 ${config.textColor} border border-current/30`}
                        key={statusKey}
                      >
                        {String(stats.byStatus[statusKey] ?? 0)} {config.title}
                      </span>
                    ),
                  )}
                  <Button
                    className="p-1.5 hover:scale-110 transition-all duration-200 relative z-10"
                    onClick={() => {
                      handleNewTask(workspace);
                    }}
                    size="sm"
                    title={`Add new task to ${workspace}`}
                    variant="primary"
                  >
                    <LucidePlus className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </Card>

            {/* Status Columns */}
            {renderStatusColumns(
              filteredTasks,
              statusConfig,
              deleteTask,
              setEditingTask,
              setShowTaskModal,
              handleDragStart,
              handleDragEnd,
              sensors,
              overlayTask,
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * WHAT:
 *   Kanban board component that provides drag-and-drop task management with
 *   status columns, workspace grouping, and task filtering.
 *
 * WHY:
 *   Enables visual task organization and status management with intuitive
 *   drag-and-drop interface.
 *
 * BEHAVIOR:
 *   - Groups tasks by workspace and status
 *   - Supports drag-and-drop for status changes and reordering
 *   - Filters tasks by search string
 *   - Updates task status on column change
 *   - Updates task scores on reordering
 *   - Displays drag overlay during drag
 *
 * RISKS:
 *   - NOTE: Drag-and-drop may have performance issues with many tasks
 *   - NOTE: Task status update on drag may fail silently
 *   - NOTE: Filter search may not work correctly with special characters
 *   - NOTE: Composite ID format may cause issues if task IDs contain pipe character
 *
 * @param {Properties} props - Component props
 *
 * @returns {JSX.Element} Kanban board component
 *
 */
export const TasksKanbanBoard: FC<Properties> = ({
  deleteTask: deleteTaskProp,
  setEditingTask,
  setPreselectedWorkspace,
  setShowTaskModal,
  setTaskFilter,
  taskFilter,
  tasks,
  updateTask,
  workspaceList = defaultWorkspaces,
}) => {
  // NOTE: DnD Sensors - pointer and keyboard support
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  // NOTE: Overlay state for drag preview
  const [overlayTask, setOverlayTask] = useState<SupabaseTask | undefined>();

  /**
   * WHAT:
   *   Handles drag start event.
   *
   * WHY:
   *   Sets up drag overlay with task preview.
   *
   * @param root0
   *
   * @param root0.active
   *
   */
  const handleDragStart = ({ active }: DragStartEvent) => {
    // NOTE: Extract task ID from composite ID (taskId|status)
    const [taskId] = String(active.id).split("|");
    const task = tasks.find((t) => t.id === taskId) ?? null;
    setOverlayTask(task ?? undefined);
  };

  /**
   * WHAT:
   *   Handles drag end event.
   *
   * WHY:
   *   Updates task status or reorders tasks based on drop location.
   *
   * BEHAVIOR:
   *   - If dropped in different column: updates task status
   *   - If dropped in same column: reorders tasks and updates scores
   *
   * @param event
   *
   */
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setOverlayTask(undefined);

    if (!over || active.id === over.id) {
      return;
    }
    // NOTE: Extract task ID and status from composite IDs
    const [taskId, fromStatus] = String(active.id).split("|");
    const [, toStatus] = String(over.id).split("|");

    // NOTE: If status changed, update task status
    if (fromStatus !== toStatus && taskId) {
      await updateTask(taskId, { status: toStatus ?? null });
      return;
    }

    // NOTE: If same column, reorder tasks and update scores
    const filteredColumnTasks: SupabaseTask[] = tasks.filter(
      (t: SupabaseTask) => (t.status ?? "No Status") === fromStatus,
    );
    const columnTasks: SupabaseTask[] = sortTasksByScore(filteredColumnTasks);

    const oldIndex = columnTasks.findIndex((t: SupabaseTask) => t.id === taskId);
    const newIndex = columnTasks.findIndex(
      (t: SupabaseTask) => t.id === String(over.id).split("|")[0],
    );

    if (oldIndex !== newIndex) {
      const reordered = arrayMove(columnTasks, oldIndex, newIndex);
      // NOTE: Update scores based on new order
      await Promise.all(
        reordered.map((t: SupabaseTask, index: number) =>
          updateTask(t.id, { score: reordered.length - index }),
        ),
      );
    }
  };

  // NOTE: Group tasks by workspace, then by status
  const kanbanData = useMemo((): Record<string, Record<string, SupabaseTask[]>> => {
    const workspaces: Record<string, Record<string, SupabaseTask[]>> = {};

    // NOTE: Initialize all workspaces from workspaceList with empty status arrays
    if (workspaceList.length > 0) {
      for (const workspace of workspaceList) {
        workspaces[workspace] = {
          Done: [],
          "In Progress": [],
          "Not Started": [],
        };
      }
    }

    // Add tasks to their respective workspaces
    for (const task of tasks) {
      const workspace: string = task.workspace ?? "Other";
      const status: string = task.status ?? "No Status";

      workspaces[workspace] ??= {
        Done: [],
        "In Progress": [],
        "Not Started": [],
      };

      if (workspaces[workspace][status]) {
        workspaces[workspace][status].push(task);
      }
    }

    return workspaces;
  }, [tasks, workspaceList]);

  // Get unique workspaces in correct order
  const workspaces = useMemo((): string[] => {
    // Use workspaceList if provided, otherwise fall back to sorted keys from kanbanData
    if (workspaceList.length > 0) {
      // Show all workspaces from workspaceList, even if they have no tasks
      return workspaceList;
    }
    const sortedKeys = Object.keys(kanbanData);
    sortedKeys.sort((a: string, b: string) => a.localeCompare(b));
    return sortedKeys;
  }, [kanbanData, workspaceList]);

  // Status configuration
  const statusConfig = useMemo((): Record<string, StatusConfigType> => {
    return {
      Done: {
        bgColor: "bg-green-500",
        borderColor: "border-green-500",
        color: "success",
        description: "Completed tasks",
        icon: LucideCheckCircle,
        textColor: "text-green-400",
        title: "Done",
      },
      "In Progress": {
        bgColor: "bg-yellow-500",
        borderColor: "border-yellow-500",
        color: "warning",
        description: "Currently working",
        icon: LucideClock,
        textColor: "text-yellow-400",
        title: "In Progress",
      },
      "Not Started": {
        bgColor: "bg-tracq-bright-blue",
        borderColor: "border-tracq-bright-blue",
        color: "info",
        description: "Tasks to begin",
        icon: LucideFolderOpen,
        textColor: "text-tracq-bright-blue",
        title: "Not Started",
      },
    };
  }, []);

  // Calculate workspace statistics
  const workspaceStats = useMemo((): Record<
    string,
    { total: number; byStatus: Record<string, number> }
  > => {
    const stats: Record<string, { total: number; byStatus: Record<string, number> }> = {};

    for (const workspace of workspaces) {
      const workspaceData = kanbanData[workspace];

      const visibleItems: Record<string, number> = {
        Done: workspaceData?.Done?.length ?? 0,
        "In Progress": workspaceData?.["In Progress"]?.length ?? 0,
        "Not Started": workspaceData?.["Not Started"]?.length ?? 0,
      };

      const total = Object.values(visibleItems).reduce(
        (sum: number, count: number) => sum + count,
        0,
      );
      const byStatus = visibleItems;

      stats[workspace] = { byStatus, total };
    }

    return stats;
  }, [kanbanData, workspaces]);

  const handleNewTask = (workspace: string) => {
    // Set editingTask to undefined for new tasks and preselect the workspace
    setEditingTask(undefined);
    if (setPreselectedWorkspace) {
      setPreselectedWorkspace(workspace);
    }
    try {
      setShowTaskModal(true);
    } catch {
      // NOTE: Failed to show task modal - silently ignore to prevent UI disruption
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-white">Task Board</h2>
          <p className="text-slate-400">Workspace-basierte Übersicht aller Tasks</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-slate-400">
            <LucideColumns className="w-4 h-4" />
            <span className="text-sm">Kanban View</span>
          </div>
          <Button
            className="flex items-center gap-2"
            onClick={() => {
              setEditingTask(undefined);
              if (setPreselectedWorkspace) {
                setPreselectedWorkspace(undefined);
              }
              try {
                setShowTaskModal(true);
              } catch {
                // NOTE: Failed to show task modal - silently ignore to prevent UI disruption
              }
            }}
            title="Neuen Task erstellen"
            variant="primary"
          >
            <LucidePlus className="w-4 h-4" />
            New Task
          </Button>
        </div>
      </div>

      {/* Board Statistics */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-200 hover:scale-[1.01]"
        padding="lg"
        variant="elevated"
      >
        <h3 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
          <LucideBarChart3 className="w-5 h-5 text-tracq-bright-blue" />
          Board Statistics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(statusConfig).map(([statusKey, config]) => {
            const totalItems = workspaces.reduce((sum: number, workspace: string) => {
              return sum + (kanbanData[workspace]?.[statusKey]?.length ?? 0);
            }, 0);

            return (
              <Card
                className="text-center bg-slate-600/30 border-slate-500/50 hover:border-slate-400/50 transition-all duration-200"
                key={statusKey}
                padding="md"
                variant="default"
              >
                <div className={`w-4 h-4 rounded-full mx-auto mb-3 shadow-lg ${config.bgColor}`} />
                <p className="text-slate-400 text-sm font-medium">{config.title}</p>
                <p className="text-white font-semibold text-lg">{totalItems}</p>
              </Card>
            );
          })}
        </div>
      </Card>

      {/* Search Bar */}
      <section aria-label="Task filters and search" className="space-y-6">
        <div className="relative">
          <LucideSearch
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400"
            size={18}
          />
          <input
            aria-label="Search tasks"
            className="w-full pl-12 pr-4 py-3 bg-slate-700/30 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-tracq-bright-blue/50 focus:ring-2 focus:ring-tracq-bright-blue/20 transition-all duration-200"
            onChange={(e) => {
              setTaskFilter(e.target.value);
            }}
            placeholder="Search tasks across all workspaces..."
            type="text"
            value={taskFilter}
          />
        </div>
      </section>

      {/* Workspace-based Kanban Board */}
      {renderWorkspaceBoards(
        workspaces,
        workspaceStats,
        tasks,
        taskFilter,
        statusConfig,
        deleteTaskProp,
        setEditingTask,
        setShowTaskModal,
        handleNewTask,
        handleDragStart,
        handleDragEnd,
        sensors,
        overlayTask,
      )}
    </div>
  );
};

// Named export only - no default export
